import React from "react";

import Alert from "@cloudscape-design/components/alert";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Pagination from "@cloudscape-design/components/pagination";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Table from "@cloudscape-design/components/table";
import { useRecoilValue } from "recoil";
import { userStore } from "../stores/access";
import { configStore } from "../stores/config";

const STATUS_OPTIONS = [
	{ label: "Enabled", value: "enabled" },
	{ label: "Disabled", value: "disabled" },
	{ label: "All", value: "all" },
];

class StaffCreditLimits extends React.Component {
	constructor(props) {
		super(props);

		const configuredLimit = Number(props.pageSize);
		this.state = {
			items: [],
			loading: false,
			error: "",
			filter: { search: "", status: "enabled" },
			pagination: {
				page: 1,
				limit: Math.min(50, Number.isSafeInteger(configuredLimit) && configuredLimit > 0 ? configuredLimit : 20),
				total: 0,
				totalPages: 1,
			},
			selectedItems: [],
			searchInputFocused: false,
			editItem: null,
			operationLimit: "",
			totalLimit: "",
			saveAttempted: false,
			saving: false,
			saveError: "",
			writeOffPresetsVisible: false,
			writeOffPresets: [],
			writeOffPresetsLoading: false,
			writeOffPresetsLoaded: false,
			writeOffPresetsLoadError: "",
			writeOffPresetsSaving: false,
			writeOffPresetsSaveError: "",
			writeOffPresetsSaveAttempted: false,
		};
		this.listRequestId = 0;
		this.saveRequestId = 0;
		this.writeOffPresetsRequestId = 0;
		this.writeOffPresetsSaveRequestId = 0;
		this.writeOffPresetDraftId = 0;
	}

	componentDidMount() {
		this.loadCreditLimits();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.user.login !== this.props.user.login || String(prevProps.user.brand?.id) !== String(this.props.user.brand?.id)) {
			this.saveRequestId += 1;
			this.writeOffPresetsRequestId += 1;
			this.writeOffPresetsSaveRequestId += 1;
			this.setState(
				{
					items: [],
					selectedItems: [],
					editItem: null,
					saving: false,
					saveError: "",
					writeOffPresetsVisible: false,
					writeOffPresets: [],
					writeOffPresetsLoading: false,
					writeOffPresetsLoaded: false,
					writeOffPresetsLoadError: "",
					writeOffPresetsSaving: false,
					writeOffPresetsSaveError: "",
					writeOffPresetsSaveAttempted: false,
					filter: { search: "", status: "enabled" },
					pagination: { ...this.state.pagination, page: 1, total: 0, totalPages: 1 },
				},
				() => this.loadCreditLimits(),
			);
		}
	}

	componentWillUnmount() {
		this.listRequestId += 1;
		this.saveRequestId += 1;
		this.writeOffPresetsRequestId += 1;
		this.writeOffPresetsSaveRequestId += 1;
	}

	getErrorText(error, fallback) {
		return (error && (error.text || error.message)) || fallback;
	}

	normalizeItem(item) {
		if (
			!item ||
			typeof item !== "object" ||
			Array.isArray(item) ||
			typeof item.login !== "string" ||
			!item.login ||
			!Number.isSafeInteger(item.level) ||
			item.level < 0 ||
			!Array.isArray(item.projects) ||
			!Array.isArray(item.groups) ||
			typeof item.enabled !== "boolean" ||
			!["operationLimitMinor", "totalLimitMinor", "usedMinor", "remainingMinor"].every((field) => Number.isSafeInteger(item[field]) && item[field] >= 0)
		) {
			throw new Error("Invalid credit limits response");
		}

		if (
			(item.enabled && (item.operationLimitMinor <= 0 || item.totalLimitMinor <= 0)) ||
			(!item.enabled && (item.operationLimitMinor !== 0 || item.totalLimitMinor !== 0 || item.usedMinor !== 0 || item.remainingMinor !== 0)) ||
			item.operationLimitMinor > item.totalLimitMinor ||
			item.usedMinor > item.totalLimitMinor ||
			item.remainingMinor !== item.totalLimitMinor - item.usedMinor
		) {
			throw new Error("Invalid credit limits response");
		}

		return {
			login: item.login,
			level: item.level,
			projects: item.projects,
			groups: item.groups,
			enabled: item.enabled,
			operationLimitMinor: item.operationLimitMinor,
			totalLimitMinor: item.totalLimitMinor,
			usedMinor: item.usedMinor,
			remainingMinor: item.remainingMinor,
		};
	}

	loadCreditLimits(overrides = {}) {
		this.listRequestId += 1;
		const requestId = this.listRequestId;
		const login = this.props.user.login;
		const brand = this.props.user.brand?.id;
		const filter = {
			...this.state.filter,
			...(overrides.filter || {}),
		};
		const page = typeof overrides.page === "undefined" ? this.state.pagination.page : overrides.page;
		const requestedLimit = Number(overrides.limit);
		const limit = Number.isSafeInteger(requestedLimit) && requestedLimit > 0 ? Math.min(50, requestedLimit) : this.state.pagination.limit;
		const payload = {
			page,
			limit,
			status: filter.status,
		};
		if (filter.search) payload.search = filter.search;

		this.setState({
			loading: true,
			error: "",
			filter,
			pagination: { ...this.state.pagination, page, limit },
			selectedItems: [],
		});

		this.props.Interface.request("get", { class: "settings", module: "staff", method: "creditLimits" }, payload)
			.then((response) => {
				if (requestId !== this.listRequestId || this.props.user.login !== login || String(this.props.user.brand?.id) !== String(brand)) return;
				if (!response || !Array.isArray(response.items) || !response.pagination || typeof response.pagination !== "object") {
					throw new Error("Invalid credit limits response");
				}

				const items = response.items.map((item) => this.normalizeItem(item));
				const { page: responsePage, limit: responseLimit, total, totalPages } = response.pagination;
				if (
					!Number.isSafeInteger(responsePage) ||
					responsePage < 1 ||
					!Number.isSafeInteger(responseLimit) ||
					responseLimit < 1 ||
					!Number.isSafeInteger(total) ||
					total < 0 ||
					!Number.isSafeInteger(totalPages) ||
					totalPages < 0
				) {
					throw new Error("Invalid credit limits response");
				}
				this.setState({
					items,
					loading: false,
					error: "",
					pagination: {
						page: responsePage,
						limit: responseLimit,
						total,
						totalPages,
					},
				});
			})
			.catch((error) => {
				if (requestId !== this.listRequestId || this.props.user.login !== login || String(this.props.user.brand?.id) !== String(brand)) return;
				const errorText = this.getErrorText(error, "Failed to load credit limits");
				this.setState({ loading: false, error: errorText });
				if (!error?.timestamp) this.props.Interface.addNoty("Error", errorText);
			});
	}

	parseRubMinor(value) {
		if (typeof value !== "string" || !/^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,2})?$/.test(value)) return null;

		const parts = value.split(".");
		const minor = Number(parts[0] + (parts[1] || "").padEnd(2, "0"));
		return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
	}

	formatMinorInput(amountMinor) {
		const digits = String(amountMinor).padStart(3, "0");
		return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
	}

	formatMinor(amountMinor) {
		const value = this.formatMinorInput(amountMinor);
		const parts = value.split(".");
		return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")},${parts[1]} RUB`;
	}

	getEditErrors() {
		const item = this.state.editItem;
		const operationLimitMinor = this.parseRubMinor(this.state.operationLimit);
		const totalLimitMinor = this.parseRubMinor(this.state.totalLimit);
		let operation = "";
		let total = "";

		if (operationLimitMinor === null) operation = "Enter a positive RUB amount with no more than two decimal digits";
		if (totalLimitMinor === null) total = "Enter a positive RUB amount with no more than two decimal digits";
		if (operationLimitMinor !== null && totalLimitMinor !== null && operationLimitMinor > totalLimitMinor) {
			operation = "Operation limit cannot exceed total limit";
		}
		if (item?.enabled && totalLimitMinor !== null && totalLimitMinor < item.totalLimitMinor) {
			total = "Total limit cannot be decreased";
		}
		if (item?.enabled && totalLimitMinor !== null && totalLimitMinor < item.usedMinor) {
			total = "Total limit cannot be less than used amount";
		}

		return { operation, total, operationLimitMinor, totalLimitMinor };
	}

	openEditor(item) {
		this.setState({
			editItem: item,
			operationLimit: item.enabled ? this.formatMinorInput(item.operationLimitMinor) : "",
			totalLimit: item.enabled ? this.formatMinorInput(item.totalLimitMinor) : "",
			saveAttempted: false,
			saving: false,
			saveError: "",
		});
	}

	closeEditor() {
		if (this.state.saving) return;
		this.setState({ editItem: null, saveAttempted: false, saveError: "" });
	}

	saveCreditLimit() {
		const item = this.state.editItem;
		if (!item || this.state.saving) return;

		const validation = this.getEditErrors();
		if (validation.operation || validation.total) {
			this.setState({ saveAttempted: true });
			return;
		}

		this.saveRequestId += 1;
		const requestId = this.saveRequestId;
		const listRequestId = this.listRequestId;
		const loginContext = this.props.user.login;
		const brandContext = this.props.user.brand?.id;
		const targetLogin = item.login;
		this.setState({ saving: true, saveAttempted: true, saveError: "" });

		this.props.Interface.request(
			"set",
			{ class: "settings", module: "staff", method: "saveCreditLimit" },
			{
				login: targetLogin,
				operationLimitMinor: validation.operationLimitMinor,
				totalLimitMinor: validation.totalLimitMinor,
			},
		)
			.then((response) => {
				if (requestId !== this.saveRequestId || this.props.user.login !== loginContext || String(this.props.user.brand?.id) !== String(brandContext)) return;
				const savedItem = this.normalizeItem(response);
				const canApplyToList = listRequestId === this.listRequestId;
				this.setState((state) => ({
					items: canApplyToList ? state.items.map((current) => (current.login === savedItem.login ? savedItem : current)) : state.items,
					selectedItems: canApplyToList ? state.selectedItems.map((current) => (current.login === savedItem.login ? savedItem : current)) : [],
					editItem: state.editItem?.login === targetLogin ? null : state.editItem,
					saving: false,
					saveAttempted: false,
					saveError: "",
				}));
				this.props.Interface.addNoty("Success", `Credit limits saved for ${targetLogin}`);
			})
			.catch((error) => {
				if (requestId !== this.saveRequestId || this.props.user.login !== loginContext || String(this.props.user.brand?.id) !== String(brandContext) || this.state.editItem?.login !== targetLogin) return;
				const errorText = this.getErrorText(error, "Failed to save credit limits");
				this.setState({ saving: false, saveError: errorText });
				if (!error?.timestamp) this.props.Interface.addNoty("Error", errorText);
			});
	}

	createWriteOffPresetDraft(label = "", targetRub = "") {
		this.writeOffPresetDraftId += 1;
		return {
			id: `write-off-preset-${this.writeOffPresetDraftId}`,
			label,
			targetRub,
		};
	}

	normalizeWriteOffPresets(presets) {
		if (!Array.isArray(presets)) throw new Error("Invalid write-off presets response");

		return presets.map((preset) => {
			if (
				!preset ||
				typeof preset !== "object" ||
				Array.isArray(preset) ||
				typeof preset.label !== "string" ||
				!preset.label.trim() ||
				!Number.isSafeInteger(preset.targetMinor) ||
				preset.targetMinor <= 0 ||
				preset.targetMinor % 100 !== 0
			) {
				throw new Error("Invalid write-off presets response");
			}

			return this.createWriteOffPresetDraft(preset.label, String(preset.targetMinor / 100));
		});
	}

	openWriteOffPresets() {
		if (this.state.writeOffPresetsSaving) return;

		this.writeOffPresetsRequestId += 1;
		this.writeOffPresetsSaveRequestId += 1;
		this.setState(
			{
				writeOffPresetsVisible: true,
				writeOffPresets: [],
				writeOffPresetsLoading: true,
				writeOffPresetsLoaded: false,
				writeOffPresetsLoadError: "",
				writeOffPresetsSaving: false,
				writeOffPresetsSaveError: "",
				writeOffPresetsSaveAttempted: false,
			},
			() => this.loadWriteOffPresets(),
		);
	}

	closeWriteOffPresets() {
		if (this.state.writeOffPresetsSaving) return;

		this.writeOffPresetsRequestId += 1;
		this.writeOffPresetsSaveRequestId += 1;
		this.setState({
			writeOffPresetsVisible: false,
			writeOffPresetsLoading: false,
			writeOffPresetsSaveError: "",
			writeOffPresetsSaveAttempted: false,
		});
	}

	loadWriteOffPresets() {
		this.writeOffPresetsRequestId += 1;
		const requestId = this.writeOffPresetsRequestId;
		const login = this.props.user.login;
		const brand = this.props.user.brand?.id;
		this.setState({
			writeOffPresetsLoading: true,
			writeOffPresetsLoaded: false,
			writeOffPresetsLoadError: "",
			writeOffPresetsSaveError: "",
			writeOffPresetsSaveAttempted: false,
		});

		this.props.Interface.request("get", { class: "settings", module: "staff", method: "writeOffPresets" })
			.then((presets) => {
				const normalizedPresets = this.normalizeWriteOffPresets(presets);
				if (
					requestId !== this.writeOffPresetsRequestId ||
					!this.state.writeOffPresetsVisible ||
					this.props.user.login !== login ||
					String(this.props.user.brand?.id) !== String(brand)
				) return;

				this.setState({
					writeOffPresets: normalizedPresets,
					writeOffPresetsLoading: false,
					writeOffPresetsLoaded: true,
					writeOffPresetsLoadError: "",
					writeOffPresetsSaveError: "",
				});
			})
			.catch((error) => {
				if (
					requestId !== this.writeOffPresetsRequestId ||
					!this.state.writeOffPresetsVisible ||
					this.props.user.login !== login ||
					String(this.props.user.brand?.id) !== String(brand)
				) return;

				const errorText = this.getErrorText(error, "Failed to load write-off presets");
				this.setState({
					writeOffPresets: [],
					writeOffPresetsLoading: false,
					writeOffPresetsLoaded: false,
					writeOffPresetsLoadError: errorText,
				});
				if (!error?.timestamp) this.props.Interface.addNoty("Error", errorText);
			});
	}

	addWriteOffPreset() {
		if (!this.state.writeOffPresetsLoaded || this.state.writeOffPresetsSaving) return;

		this.setState((state) => ({
			writeOffPresets: [...state.writeOffPresets, this.createWriteOffPresetDraft()],
			writeOffPresetsSaveError: "",
		}));
	}

	updateWriteOffPreset(id, patch) {
		if (this.state.writeOffPresetsSaving) return;

		this.setState((state) => ({
			writeOffPresets: state.writeOffPresets.map((preset) => (preset.id === id ? { ...preset, ...patch } : preset)),
			writeOffPresetsSaveError: "",
		}));
	}

	removeWriteOffPreset(id) {
		if (this.state.writeOffPresetsSaving) return;

		this.setState((state) => ({
			writeOffPresets: state.writeOffPresets.filter((preset) => preset.id !== id),
			writeOffPresetsSaveError: "",
		}));
	}

	getWriteOffPresetValidation() {
		const labelCounts = new Map();
		const normalizedRows = this.state.writeOffPresets.map((preset) => {
			const label = typeof preset.label === "string" ? preset.label.trim() : "";
			const normalizedLabel = label.toLowerCase();
			if (normalizedLabel) labelCounts.set(normalizedLabel, (labelCounts.get(normalizedLabel) || 0) + 1);

			return {
				label,
				normalizedLabel,
				targetRub: typeof preset.targetRub === "string" ? preset.targetRub : "",
			};
		});
		const errors = normalizedRows.map((row) => {
			let label = "";
			let target = "";
			let targetMinor = null;

			if (!row.label) label = "Label is required";
			else if (labelCounts.get(row.normalizedLabel) > 1) label = "Labels must be unique (case-insensitive)";

			if (!/^[0-9]+$/.test(row.targetRub)) {
				target = "Enter a positive whole number of RUB";
			} else {
				const targetRub = Number(row.targetRub);
				const convertedTargetMinor = targetRub * 100;
				if (!Number.isSafeInteger(targetRub) || targetRub <= 0 || !Number.isSafeInteger(convertedTargetMinor)) {
					target = "Enter a positive whole number of RUB that can be safely saved";
				} else {
					targetMinor = convertedTargetMinor;
				}
			}

			return { label, target, targetMinor };
		});

		return {
			errors,
			valid: errors.every((error) => !error.label && !error.target),
			payload: normalizedRows.map((row, index) => ({
				label: row.label,
				targetMinor: errors[index].targetMinor,
			})),
		};
	}

	saveWriteOffPresets() {
		if (
			this.state.writeOffPresetsSaving ||
			this.state.writeOffPresetsLoading ||
			!this.state.writeOffPresetsLoaded ||
			this.state.writeOffPresetsLoadError
		) return;

		const validation = this.getWriteOffPresetValidation();
		if (!validation.valid) {
			this.setState({ writeOffPresetsSaveAttempted: true });
			return;
		}

		this.writeOffPresetsSaveRequestId += 1;
		const requestId = this.writeOffPresetsSaveRequestId;
		const login = this.props.user.login;
		const brand = this.props.user.brand?.id;
		this.setState({
			writeOffPresetsSaving: true,
			writeOffPresetsSaveAttempted: true,
			writeOffPresetsSaveError: "",
		});

		this.props.Interface.request(
			"set",
			{ class: "settings", module: "staff", method: "saveWriteOffPresets" },
			validation.payload,
		)
			.then((presets) => {
				const normalizedPresets = this.normalizeWriteOffPresets(presets);
				if (
					requestId !== this.writeOffPresetsSaveRequestId ||
					this.props.user.login !== login ||
					String(this.props.user.brand?.id) !== String(brand)
				) return;

				this.setState({
					writeOffPresetsVisible: false,
					writeOffPresets: normalizedPresets,
					writeOffPresetsLoading: false,
					writeOffPresetsLoaded: true,
					writeOffPresetsLoadError: "",
					writeOffPresetsSaving: false,
					writeOffPresetsSaveError: "",
					writeOffPresetsSaveAttempted: false,
				});
				this.props.Interface.addNoty("Success", "Write-off presets saved");
			})
			.catch((error) => {
				if (
					requestId !== this.writeOffPresetsSaveRequestId ||
					!this.state.writeOffPresetsVisible ||
					this.props.user.login !== login ||
					String(this.props.user.brand?.id) !== String(brand)
				) return;

				const errorText = this.getErrorText(error, "Failed to save write-off presets");
				this.setState({ writeOffPresetsSaving: false, writeOffPresetsSaveError: errorText });
				if (!error?.timestamp) this.props.Interface.addNoty("Error", errorText);
			});
	}

	renderWriteOffPresetsModal() {
		const validation = this.getWriteOffPresetValidation();
		const saveDisabled =
			this.state.writeOffPresetsLoading ||
			!this.state.writeOffPresetsLoaded ||
			Boolean(this.state.writeOffPresetsLoadError) ||
			this.state.writeOffPresetsSaving;

		return (
			<Modal
				size="large"
				visible={this.state.writeOffPresetsVisible}
				onDismiss={() => this.closeWriteOffPresets()}
				header="Write-off presets"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button disabled={this.state.writeOffPresetsSaving} onClick={() => this.closeWriteOffPresets()}>
								Cancel
							</Button>
							<Button variant="primary" loading={this.state.writeOffPresetsSaving} disabled={saveDisabled} onClick={() => this.saveWriteOffPresets()}>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}>
				{this.state.writeOffPresetsLoading ? (
					<Box textAlign="center" padding="l">
						<Spinner size="large" />
						<Box margin={{ top: "s" }}>Loading write-off presets</Box>
					</Box>
				) : this.state.writeOffPresetsLoadError ? (
					<Alert
						type="error"
						header="Error loading write-off presets"
						action={<Button onClick={() => this.loadWriteOffPresets()}>Retry</Button>}>
						{this.state.writeOffPresetsLoadError}
					</Alert>
				) : this.state.writeOffPresetsLoaded ? (
					<SpaceBetween size="m">
						{this.state.writeOffPresetsSaveError ? <Alert type="error">{this.state.writeOffPresetsSaveError}</Alert> : null}
						{this.state.writeOffPresets.length ? (
							this.state.writeOffPresets.map((preset, index) => (
								<Grid key={preset.id} gridDefinition={[{ colspan: 5 }, { colspan: 5 }, { colspan: 2 }]}>
									<FormField label="Label" errorText={this.state.writeOffPresetsSaveAttempted ? validation.errors[index].label : ""}>
										<Input
											value={preset.label}
											disabled={this.state.writeOffPresetsSaving}
											onChange={({ detail }) => this.updateWriteOffPreset(preset.id, { label: detail.value })}
										/>
									</FormField>
									<FormField label="Target balance" errorText={this.state.writeOffPresetsSaveAttempted ? validation.errors[index].target : ""}>
										<Input
											inputMode="numeric"
											type="number"
											value={preset.targetRub}
											disabled={this.state.writeOffPresetsSaving}
											onChange={({ detail }) => this.updateWriteOffPreset(preset.id, { targetRub: detail.value })}
										/>
									</FormField>
									<FormField label="Action">
										<Button stretch disabled={this.state.writeOffPresetsSaving} onClick={() => this.removeWriteOffPreset(preset.id)}>
											Remove
										</Button>
									</FormField>
								</Grid>
							))
						) : (
							<Box textAlign="center" padding="l">
								<Box variant="strong">Presets are not configured</Box>
								<Box color="text-body-secondary">Add a preset to create the configuration on save.</Box>
							</Box>
						)}
						<Button iconName="add-plus" disabled={this.state.writeOffPresetsSaving} onClick={() => this.addWriteOffPreset()}>
							Add preset
						</Button>
					</SpaceBetween>
				) : null}
			</Modal>
		);
	}

	renderEditor() {
		const item = this.state.editItem;
		const validation = this.getEditErrors();

		return (
			<Modal
				visible={Boolean(item)}
				onDismiss={() => this.closeEditor()}
				header={item ? `${item.enabled ? "Edit" : "Create"} credit limits: ${item.login}` : "Credit limits"}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button disabled={this.state.saving} onClick={() => this.closeEditor()}>
								Cancel
							</Button>
							<Button variant="primary" loading={this.state.saving} onClick={() => this.saveCreditLimit()}>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween size="m">
					{this.state.saveError ? <Alert type="error">{this.state.saveError}</Alert> : null}
					<FormField label="Operation limit" description="RUB" errorText={this.state.saveAttempted ? validation.operation : ""}>
						<Input
							inputMode="decimal"
							value={this.state.operationLimit}
							disabled={this.state.saving}
							onChange={({ detail }) => this.setState({ operationLimit: detail.value })}
						/>
					</FormField>
					<FormField label="Total limit" description="RUB" errorText={this.state.saveAttempted ? validation.total : ""}>
						<Input
							inputMode="decimal"
							value={this.state.totalLimit}
							disabled={this.state.saving}
							onChange={({ detail }) => this.setState({ totalLimit: detail.value })}
						/>
					</FormField>
					{item?.enabled ? (
						<Box>
							Used: {this.formatMinor(item.usedMinor)}. Current total limit: {this.formatMinor(item.totalLimitMinor)}. Remaining: {this.formatMinor(item.remainingMinor)}.
						</Box>
					) : null}
				</SpaceBetween>
			</Modal>
		);
	}

	render() {
		const statusOption = STATUS_OPTIONS.find((option) => option.value === this.state.filter.status) || STATUS_OPTIONS[0];
		const total = this.state.pagination.total;

		return (
			<>
				{this.state.error ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading credit limits">
							{this.state.error}
						</Alert>
					</Box>
				) : null}
				<Table
					selectionType="single"
					selectedItems={this.state.selectedItems}
					onSelectionChange={({ detail }) => this.setState({ selectedItems: detail.selectedItems })}
					items={this.state.items}
					loading={this.state.loading}
					empty="No staff found"
					pagination={
						<Pagination
							disabled={this.state.loading}
							currentPageIndex={this.state.pagination.page}
							pagesCount={Math.max(1, this.state.pagination.totalPages)}
							onChange={({ detail }) => this.loadCreditLimits({ page: detail.currentPageIndex })}
						/>
					}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize: this.state.pagination.limit }}
							pageSizePreference={{
								title: "Rows per page",
								options: [
									{ value: 10, label: "10" },
									{ value: 20, label: "20" },
									{ value: 30, label: "30" },
									{ value: 40, label: "40" },
									{ value: 50, label: "50" },
								],
							}}
							onConfirm={({ detail }) => this.loadCreditLimits({ page: 1, limit: detail.pageSize })}
						/>
					}
					header={
						<Box margin={{ bottom: "m" }}>
							<Header
								counter={`(${total})`}
								description="Lifetime Credit limits shared across projects"
								actions={
									<SpaceBetween direction="horizontal" size="xs">
										<Button onClick={() => this.openWriteOffPresets()}>
											Write-off presets
										</Button>
										<Button disabled={this.state.selectedItems.length !== 1 || this.state.loading} onClick={() => this.openEditor(this.state.selectedItems[0])}>
											{this.state.selectedItems[0]?.enabled ? "Edit limits" : "Create limits"}
										</Button>
									</SpaceBetween>
								}>
								Credit limits
							</Header>
						</Box>
					}
					filter={
						<div style={{ width: "800px", maxWidth: "100%" }}>
							<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
								<FormField label="User">
									<Input
										type="search"
										autoComplete="new-password"
										readOnly={!this.state.searchInputFocused}
										value={this.state.filter.search}
										placeholder="Search by login"
										onFocus={() => this.setState({ searchInputFocused: true })}
										onBlur={() => this.setState({ searchInputFocused: false })}
										onChange={({ detail }) => {
											if (!this.state.searchInputFocused) return;
											this.loadCreditLimits({ page: 1, filter: { search: detail.value } });
										}}
									/>
								</FormField>
								<FormField label="Status">
									<Select selectedOption={statusOption} options={STATUS_OPTIONS} onChange={({ detail }) => this.loadCreditLimits({ page: 1, filter: { status: detail.selectedOption.value } })} />
								</FormField>
							</Grid>
						</div>
					}
					columnDefinitions={[
						{ id: "login", header: "Login", cell: (item) => item.login },
						{
							id: "projects",
							header: "Projects",
							cell: (item) =>
								item.projects
									.map((project) => project.brandName || (project.brand ? `#${project.brand}` : ""))
									.filter(Boolean)
									.join(", ") || "—",
						},
						{ id: "operationLimit", header: "Operation limit", cell: (item) => (item.enabled ? this.formatMinor(item.operationLimitMinor) : "—") },
						{ id: "totalLimit", header: "Total limit", cell: (item) => (item.enabled ? this.formatMinor(item.totalLimitMinor) : "—") },
						{ id: "used", header: "Used", cell: (item) => (item.enabled ? this.formatMinor(item.usedMinor) : "—") },
						{ id: "remaining", header: "Remaining", cell: (item) => (item.enabled ? this.formatMinor(item.remainingMinor) : "—") },
						{ id: "status", header: "Status", cell: (item) => (item.enabled ? <Badge color="green">Enabled</Badge> : <Badge color="grey">Disabled</Badge>) },
					]}
				/>
				{this.renderEditor()}
				{this.renderWriteOffPresetsModal()}
			</>
		);
	}
}

export default function StaffCreditLimitsWithRecoil(props) {
	const user = useRecoilValue(userStore);
	const config = useRecoilValue(configStore);

	return <StaffCreditLimits {...props} user={user} pageSize={config?.staff?.perPage} />;
}
